import { getContext } from '@/context';

/**
 * Forces the host to hand over a fresh access token.
 *
 * Needed before a generation that fires off the back of a login. `isAuthorized`
 * flips as soon as the host reports the login, but the new token lands a beat
 * later — the race `usePendingUploads` notes and `uploadToCDN` absorbs by
 * refreshing on 401.
 *
 * A submit sent inside that window carries the *previous* identity's token.
 * That token is still valid, so the orchestrator answers **402 "You don't have
 * enough available credits"** rather than 401: the request is authenticated,
 * just as somebody with no credits. Nothing recovers it either — the SDK's
 * fetch wrapper auto-refreshes on 401 only, so a stale-but-valid token is never
 * replaced, and the user sees a credit failure on an account that has credits.
 *
 * Awaiting a refresh first closes the window: the request that follows is
 * guaranteed to carry the logged-in user's token.
 *
 * Failures are swallowed deliberately. A refresh that cannot complete should
 * not block the generation — the attempt proceeds on whatever token exists and
 * fails no worse than it does today. The SDK also dedupes concurrent refreshes,
 * so calling this alongside its own retry costs nothing.
 */
const refreshAuthToken = async (): Promise<void> => {
  try {
    await getContext().handlers.refreshToken();
  } catch {
    // Non-fatal: fall through and let the request try with the current token.
  }
};

export default refreshAuthToken;
